import { useEffect, useRef, useState } from 'react';

interface HiddenTerminalProps {
  onClose: () => void;
}

const TERMINAL_LINES = [
  '',
  '  ███╗   ███╗ █████╗ ████████╗██╗███████╗███████╗███╗   ███╗ █████╗',
  '  ████╗ ████║██╔══██╗╚══██╔══╝██║██╔════╝██╔════╝████╗ ████║██╔══██╗',
  '  ██╔████╔██║███████║   ██║   ██║█████╗  █████╗  ██╔████╔██║███████║',
  '  ██║╚██╔╝██║██╔══██║   ██║   ██║██╔══╝  ██╔══╝  ██║╚██╔╝██║██╔══██║',
  '  ██║ ╚═╝ ██║██║  ██║   ██║   ██║██║     ███████╗██║ ╚═╝ ██║██║  ██║',
  '  ╚═╝     ╚═╝╚═╝  ╚═╝   ╚═╝   ╚═╝╚═╝     ╚══════╝╚═╝     ╚═╝╚═╝  ╚═╝',
  '',
  '  > system: online',
  '  > kernel: matifema/1.0',
  '  > location: roma, italy (41.9028° N, 12.4964° E)',
  '  > uptime: since 2001',
  ' ',
  ' ',
  '  ? = "help"',
  '',
];

const PLAYLISTS = [
  {
    embedUrl: 'https://open.spotify.com/embed/playlist/6KGhll6GqZfXvplMAMw5zp',
    openUrl: 'https://open.spotify.com/playlist/6KGhll6GqZfXvplMAMw5zp?si=nZnL4MgNREGyPvmHoS1msQ&pi=aR-PjjMfQLOtu',
  },
  {
    embedUrl:'https://open.spotify.com/embed/playlist/0Nak9PvmefFkW1uAVGqeZt',
    openUrl:'https://open.spotify.com/playlist/0Nak9PvmefFkW1uAVGqeZt?si=D1KMASm-TUerPPlKbQTbPQ'
  },
  {
    embedUrl:'https://open.spotify.com/embed/playlist/7L1gbB15EOodwW6GGfjaNR',
    openUrl:'https://open.spotify.com/playlist/7L1gbB15EOodwW6GGfjaNR?si=sWl12WokRnuU-xNO0p0H2A'
  },
  {
    embedUrl:'https://open.spotify.com/embed/playlist/5B4qz3JL3bUUIqwwxnD0Sw',
    openUrl:'https://open.spotify.com/playlist/5B4qz3JL3bUUIqwwxnD0Sw?si=Z_QmkYW-SW27Ad2VXNMTZQ'
  }
];

const COMMANDS: Record<string, string[]> = {
  help: [
    '  available commands:',
    '    help       show this message',
    '    contact    show contact info',
    '    music      open music player',
    '    clear      clear screen',
    '    exit       close terminal',
  ],
  contact: [
    '  email:  marcoferri462@gmail.com',
    '  github: github.com/matifema',
    '  linkedin: linkedin.com/in/marco-t-ferri-marini',
    '  medium: medium.com/@marcoferri462',
  ],
  clear: [],
};

export function HiddenTerminal({ onClose }: HiddenTerminalProps) {
  const [lines, setLines] = useState<string[]>([...TERMINAL_LINES]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<'terminal' | 'music'>('terminal');
  const [musicIndex, setMusicIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mode === 'terminal') {
      inputRef.current?.focus();
    }
  }, [mode]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines, mode, musicIndex]);

  // Music player keyboard navigation
  useEffect(() => {
    if (mode !== 'music') return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMusicIndex((prev) => (prev - 1 + PLAYLISTS.length) % PLAYLISTS.length);
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMusicIndex((prev) => (prev + 1) % PLAYLISTS.length);
      }
      if (e.key === 'Enter') {
        window.open(PLAYLISTS[musicIndex].openUrl, '_blank', 'noopener,noreferrer');
      }
      if (e.key === 'Escape') {
        setMode('terminal');
        setLines((prev) => [...prev, '', '  exited music player.', '']);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, musicIndex]);

  const handleCommand = (cmd: string) => {
    const trimmed = cmd.trim().toLowerCase();
    const newLines = [...lines, `  > ${cmd}`];

    if (trimmed === 'exit' || trimmed === 'quit') {
      setTimeout(onClose, 300);
      newLines.push('', '  closing connection...');
      setLines(newLines);
      return;
    }

    if (trimmed === 'clear') {
      setLines(['']);
      return;
    }

    if (trimmed === 'music') {
      setLines([...newLines, '', '  opening music player...', '']);
      setMode('music');
      setMusicIndex(0);
      return;
    }

    if (COMMANDS[trimmed]) {
      newLines.push(...COMMANDS[trimmed]);
    } else if (trimmed) {
      newLines.push(`  unknown command: "${trimmed}". type "help" for available commands.`);
    }

    newLines.push('');
    setLines(newLines);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCommand(input);
      setInput('');
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(10, 10, 10, 0.95)',
        fontFamily: "'JetBrains Mono', monospace",
        color: '#33ff33',
        display: 'flex',
        flexDirection: 'column',
        padding: '2rem',
        overflow: 'hidden',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{
        flex: 1,
        overflowY: 'auto',
        fontSize: '0.75rem',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        maxWidth: '48rem',
        margin: '0 auto',
        width: '100%',
      }}>
        {lines.map((line, i) => (
          <div key={i}>{line || '\u00A0'}</div>
        ))}

        {mode === 'terminal' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#33ff33' }}>&gt;</span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              autoComplete="off"
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#33ff33',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.75rem',
                flex: 1,
                caretColor: '#33ff33',
              }}
            />
            <span className="cursor-blink" style={{ color: '#33ff33' }}>_</span>
          </div>
        )}

        {mode === 'music' && (
          <div style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>playlists:</div>
            {PLAYLISTS.map((pl, i) => (
              <div
                key={i}
                onClick={() => window.open(pl.openUrl, '_blank', 'noopener,noreferrer')}
                style={{
                  padding: '0.5rem',
                  border: '1px solid',
                  borderColor: i === musicIndex ? '#33ff33' : '#1a5a1a',
                  marginBottom: '0.75rem',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  width: 'fit-content',
                }}
              >
                {i === musicIndex && (
                  <iframe
                    src={pl.embedUrl}
                    width="300"
                    height="80"
                    style={{ border: 0, borderRadius: '4px', display: 'block' }}
                    allow="encrypted-media"
                  />
                )}
              </div>
            ))}
            <div style={{ fontSize: '0.5625rem', color: '#1a5a1a', marginTop: '1rem' }}>
              arrows to navigate · enter or tap to open · esc to go back
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>
      
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.5625rem',
        color: '#1a5a1a',
        textAlign: 'center',
        paddingTop: '1rem',
      }}>
        press ESC or type "exit" to close · ctrl+shift+k to reopen · type "music" for playlists · konami code for crt mode
      </div>
    </div>
  );
}
